import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { GANTT_VIEW_TYPES } from '../../../shared/appConstants';
import GanttTaskCapsule from './GanttTaskCapsule';
import GanttTaskCard from './GanttTaskCard';
import GanttDependencyArrows from './GanttDependencyArrows';
import { calculateTaskPositions, calculateCardPositions, calculateMultiMonthPosition, calculateTodayPosition, calculateTaskDuration, getTaskColumnRange } from './ganttUtils';
import './GanttGrid.scss';

const GanttGrid = ({
  dates,
  headers,
  tasks,
  viewType,
  onTaskClick,
  onBoundaryClick,
  configOptions = {},
  groupByOption = 'taskType',
  projectId,
  filters,
  pagination,
  initialScrollLeft = 0,
  onTimelineScrollChange
}) => {
  const [localTasks, setLocalTasks] = useState({});
  const [dragState, setDragState] = useState(null);
  const [dropZone, setDropZone] = useState(null);
  const [weekDropZone, setWeekDropZone] = useState(null); // For week view day-level drop zones
  const [resizeState, setResizeState] = useState(null);
  const [scrollLeft, setScrollLeft] = useState(initialScrollLeft || 0);
  const [bodyReady, setBodyReady] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [activeTooltipTaskId, setActiveTooltipTaskId] = useState(null);
  const gridBodyRef = useRef(null);
  const gridHeaderRef = useRef(null);
  const lastScrollLeftRef = useRef(initialScrollLeft || 0);
  const savedScrollRef = useRef(initialScrollLeft || 0);
  const isPaginationRef = useRef(false);
  const isSyncingRef = useRef(false);
  const rafIdRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const paginationStateRef = useRef({
    currentPage: pagination?.currentPage,
    lastAppliedPage: pagination?.currentPage,
    lastAppliedTasks: null,
    savedScroll: initialScrollLeft || 0,
    isPaginating: false
  });

  // Use tasks from props for display, Redux store handles optimistic updates
  const currentTasks = tasks;

  // Mount-only setup: body readiness, scroll syncing, and tooltip hiding on scroll
  useEffect(() => {
    const bodyElement = gridBodyRef.current;
    const headerElement = gridHeaderRef.current;
    let scrollTimeout;

    if (bodyElement) {
      if (initialScrollLeft > 0) {
        bodyElement.scrollLeft = initialScrollLeft;
        if (headerElement) {
          headerElement.scrollLeft = initialScrollLeft;
        }
        setScrollLeft(initialScrollLeft);
      }
      setBodyReady(true);
    }

    const handleBodyScroll = () => {
      if (!bodyElement || !headerElement) return;
      if (isSyncingRef.current) return;
      if (isPaginationRef.current) return;
      const newScrollLeft = bodyElement.scrollLeft;
      headerElement.scrollLeft = newScrollLeft;
      lastScrollLeftRef.current = newScrollLeft;
      savedScrollRef.current = newScrollLeft;
      paginationStateRef.current.savedScroll = newScrollLeft;
      onTimelineScrollChange?.(newScrollLeft);
      // Only update state if horizontal scroll actually changed
      setScrollLeft(prev => prev !== newScrollLeft ? newScrollLeft : prev);
    };

    const handleHeaderScroll = () => {
      if (!bodyElement || !headerElement) return;
      if (isSyncingRef.current) return;
      if (isPaginationRef.current) return;
      const newScrollLeft = headerElement.scrollLeft;
      bodyElement.scrollLeft = newScrollLeft;
      lastScrollLeftRef.current = newScrollLeft;
      savedScrollRef.current = newScrollLeft;
      paginationStateRef.current.savedScroll = newScrollLeft;
      onTimelineScrollChange?.(newScrollLeft);
      // Only update state if horizontal scroll actually changed
      setScrollLeft(prev => prev !== newScrollLeft ? newScrollLeft : prev);
    };

    const handleGridScroll = () => {
      if (!bodyElement) return;
      setActiveTooltipTaskId(null);
      // Hide all tooltips immediately when scrolling starts
      document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
        tooltip.style.display = 'none';
      });

      // Add scrolling class
      bodyElement.classList.add('scrolling');

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Restore tooltips after scrolling stops
      scrollTimeout = setTimeout(() => {
        bodyElement.classList.remove('scrolling');
        document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
          tooltip.style.display = '';
        });
      }, 150);
    };

    if (bodyElement && headerElement) {
      bodyElement.addEventListener('scroll', handleBodyScroll);
      headerElement.addEventListener('scroll', handleHeaderScroll);
    }

    if (bodyElement) {
      bodyElement.addEventListener('scroll', handleGridScroll, { passive: true });
    }

    return () => {
      if (bodyElement && headerElement) {
        bodyElement.removeEventListener('scroll', handleBodyScroll);
        headerElement.removeEventListener('scroll', handleHeaderScroll);
      }
      if (bodyElement) {
        bodyElement.removeEventListener('scroll', handleGridScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTaskDates = useCallback((task) => {
    const local = localTasks[task.id];
    return {
      startDate: local?.startDate || task.startDate,
      dueDate: local?.dueDate || task.dueDate
    };
  }, [localTasks]);

  useEffect(() => {
    const taskDateMap = new Map();

    const collectTaskDates = (taskList) => {
      taskList.forEach((task) => {
        taskDateMap.set(String(task.id), {
          startDate: task.startDate || null,
          dueDate: task.dueDate || null
        });

        if (task.subtasks?.length) {
          collectTaskDates(task.subtasks);
        }
      });
    };

    collectTaskDates(tasks);

    setLocalTasks((prev) => {
      const nextEntries = Object.entries(prev);
      if (nextEntries.length === 0) return prev;

      let hasChanges = false;
      const next = { ...prev };

      nextEntries.forEach(([taskId, localDates]) => {
        const upstreamDates = taskDateMap.get(String(taskId));

        if (!upstreamDates) {
          delete next[taskId];
          hasChanges = true;
          return;
        }

        const normalizedLocalStart = localDates?.startDate || null;
        const normalizedLocalDue = localDates?.dueDate || null;
        const baseStartDate = localDates?.baseStartDate || null;
        const baseDueDate = localDates?.baseDueDate || null;

        if (
          normalizedLocalStart === upstreamDates.startDate &&
          normalizedLocalDue === upstreamDates.dueDate
        ) {
          delete next[taskId];
          hasChanges = true;
          return;
        }

        if (
          upstreamDates.startDate === baseStartDate &&
          upstreamDates.dueDate === baseDueDate
        ) {
          return;
        }

        next[taskId] = {
          ...localDates,
          ...upstreamDates,
          baseStartDate: upstreamDates.startDate,
          baseDueDate: upstreamDates.dueDate
        };
        hasChanges = true;
      });

      return hasChanges ? next : prev;
    });
  }, [tasks]);

  const handleTaskUpdate = (taskId, newStartDate, newDueDate, currentTask = null) => {
    setLocalTasks(prev => ({
      ...prev,
      [taskId]: {
        startDate: newStartDate,
        dueDate: newDueDate,
        baseStartDate: currentTask?.startDate || null,
        baseDueDate: currentTask?.dueDate || null
      }
    }));
  };

  const handleDragStart = (dragInfo) => {
    if (dragInfo.dragType === 'move') {
      setDragState(dragInfo);
    } else {
      setResizeState(dragInfo);
    }
  };

  const handleDragUpdate = (dragInfo) => {
    if (dragInfo.dragType === 'move') {
      setDragState(dragInfo);
      
      if (viewType === GANTT_VIEW_TYPES.WEEK && typeof dragInfo.dropZone === 'object') {
        // For week view, set detailed drop zone info without mouse position
        setWeekDropZone(dragInfo.dropZone);
        setDropZone(dragInfo.dropZone.columnIndex);
      } else {
        // For other views, use simple drop zone
        setDropZone(dragInfo.dropZone);
        setWeekDropZone(null);
      }
    } else if (dragInfo.dragType === 'resize-left' || dragInfo.dragType === 'resize-right') {
      setResizeState(dragInfo);
      
      if (viewType === GANTT_VIEW_TYPES.WEEK && typeof dragInfo.dropZone === 'object') {
        // For week view resize, set detailed drop zone info without mouse position
        setWeekDropZone(dragInfo.dropZone);
        setDropZone(dragInfo.dropZone.columnIndex);
      } else {
        // For other views, use simple drop zone
        setDropZone(dragInfo.dropZone);
        setWeekDropZone(null);
      }
    }
  };

  const handleBoundaryTaskClick = useCallback((boundaryInfo, direction, ticketId = null) => {
    if (onBoundaryClick) {
      onBoundaryClick(boundaryInfo, direction, ticketId);
    }
  }, [onBoundaryClick]);

  const handleDragEnd = () => {
    setDragState(null);
    setDropZone(null);
    setWeekDropZone(null);
    setResizeState(null);
  };

  const parseDateLocal = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getWeekColumnDay = (dateStr) => {
    if (!dateStr || viewType !== GANTT_VIEW_TYPES.WEEK) return null;
    const target = parseDateLocal(dateStr);
    if (!target) return null;
    for (let i = 0; i < dates.length; i += 1) {
      const weekStart = parseDateLocal(dates[i]);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      if (target >= weekStart && target <= weekEnd) {
        const dayOfWeek = target.getDay();
        const mondayBasedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        return { columnIndex: i, dayIndex: mondayBasedDay };
      }
    }
    return null;
  };

  // Group tasks based on the selected groupByOption
  const groupedTasks = useMemo(() => {
    const groups = {};

    const normalizeGroupValue = (value, fallback) => {
      if (value === null || value === undefined) return fallback;
      if (typeof value === 'string' || typeof value === 'number') return String(value).trim() || fallback;
      if (typeof value === 'object') {
        const label = value.name || value.label || value.title;
        if (label) return String(label).trim();
        if (value.id !== undefined && value.id !== null) return String(value.id);
        return fallback;
      }
      return String(value).trim() || fallback;
    };

    currentTasks.forEach(task => {
      let groupKey;
      
      switch (groupByOption) {
        case 'taskType':
          groupKey = normalizeGroupValue(task.taskType, 'Other');
          break;
        case 'priority':
          groupKey = normalizeGroupValue(task.priority, 'No Priority');
          break;
        case 'status': {
          const statusValue = task.status;
          const statusLabel = normalizeGroupValue(statusValue, 'No Status');
          groupKey = statusLabel || 'No Status';
          break;
        }
        case 'assignee': {
          const primaryAssignee = task.assignees?.[0] || task.assignee;
          if (primaryAssignee) {
            groupKey = normalizeGroupValue(primaryAssignee.name || `${primaryAssignee.first_name || ''} ${primaryAssignee.last_name || ''}`.trim(), 'Unassigned');
          } else {
            groupKey = 'Unassigned';
          }
          break;
        }
        default:
          groupKey = normalizeGroupValue(task.taskType, 'Other');
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(task);
    });

    return groups;
  }, [currentTasks, groupByOption]);

  // Flatten grouped tasks maintaining group order
  const flattenedTasks = useMemo(() => {
    const result = [];
    const sortedGroupKeys = Object.keys(groupedTasks).sort();
    
    sortedGroupKeys.forEach(groupKey => {
      groupedTasks[groupKey].forEach(task => {
        result.push(task);
      });
    });
    
    return result;
  }, [groupedTasks]);

  const tasksByColumn = useMemo(() => {
    if (viewType === GANTT_VIEW_TYPES.QUARTER || viewType === GANTT_VIEW_TYPES.YEAR) {
      return calculateCardPositions(flattenedTasks, dates, viewType, getTaskDates);
    }
    return calculateTaskPositions(flattenedTasks, dates, viewType, getTaskDates);
  }, [flattenedTasks, dates, viewType, getTaskDates]);

  const incomingDependenciesMap = useMemo(() => {
    const map = new Map();
    const seen = new Set();
    flattenedTasks.forEach(task => {
      if (seen.has(task.id)) return;
      seen.add(task.id);
      (task.dependencies || []).forEach(dep => {
        const targetId = dep.targetTaskId ?? dep.target_task_id ?? dep.target_id;
        if (targetId === undefined || targetId === null) return;
        const key = String(targetId);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ sourceTask: task, dep });
      });
    });
    return map;
  }, [flattenedTasks]);

  const taskByIdMap = useMemo(() => {
    const map = new Map();
    flattenedTasks.forEach(task => {
      map.set(String(task.id), task);
    });
    return map;
  }, [flattenedTasks]);

  useEffect(() => {
    const bodyEl = gridBodyRef.current;
    if (!bodyEl) return undefined;

    const update = () => {
      const hasScroll = bodyEl.scrollHeight > bodyEl.clientHeight;
      const sbw = bodyEl.offsetWidth - bodyEl.clientWidth;
      setScrollbarWidth(hasScroll ? sbw : 0);
    };

    update();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(bodyEl);
    }

    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [tasksByColumn, viewType, dates.length]);

  // Calculate dynamic label position based on task position
  const calculateLabelPosition = useCallback((taskId) => {
    if (!taskId) return '60px';
    
    // Find the task in tasksByColumn to get its row index
    let taskRowIndex = null;
    for (const columnTasks of Object.values(tasksByColumn)) {
      const taskItem = columnTasks?.find(item => item.task.id === taskId);
      if (taskItem) {
        taskRowIndex = taskItem.rowIndex;
        break;
      }
    }
    
    if (taskRowIndex !== null) {
      // Calculate position based on row index
      const taskTopPosition = taskRowIndex * (36 + 40); // Same calculation as in task positioning
      const containerHeight = gridBodyRef.current?.clientHeight || 800;
      
      // If task is in upper half, show label below task
      // If task is in lower half, show label above task
      if (taskTopPosition < containerHeight / 2) {
        return Math.min(taskTopPosition + 50, containerHeight - 40) + 'px';
      } else {
        return Math.max(taskTopPosition - 40, 10) + 'px';
      }
    }
    
    return '60px'; // Fallback
  }, [tasksByColumn]);

  // Calculate today's base position (without scroll adjustment)
  const todayBasePosition = useMemo(() => {
    if (!configOptions.todayIndicator) return { position: null, isVisible: false };
    return calculateTodayPosition(dates, viewType);
  }, [dates, viewType, configOptions.todayIndicator]);

  const getColumnMetrics = useCallback((colIndex) => {
    const bodyEl = gridBodyRef.current;
    if (!bodyEl && !bodyReady) {
      const paddingLeft = 20;
      const baseWidth = (viewType === GANTT_VIEW_TYPES.WEEK || viewType === GANTT_VIEW_TYPES.DAY) ? 500 : 150;
      return {
        left: paddingLeft + colIndex * baseWidth,
        width: baseWidth
      };
    }
    if (!bodyEl) return null;

    const columnEl = bodyEl.querySelector(`[data-column-index="${colIndex}"]`);
    if (columnEl) {
      const rect = columnEl.getBoundingClientRect();
      return {
        left: columnEl.offsetLeft,
        width: rect.width
      };
    }

    const styles = getComputedStyle(bodyEl);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const totalWidth = bodyEl.scrollWidth || bodyEl.clientWidth;
    const contentWidth = Math.max(1, totalWidth - paddingLeft - paddingRight);
    const columnWidth = contentWidth / dates.length;

    return {
      left: paddingLeft + colIndex * columnWidth,
      width: columnWidth
    };
  }, [dates.length, viewType, bodyReady]);

  const cancelPendingScroll = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const applyScrollToTarget = useCallback((targetScroll, targetPage) => {
    cancelPendingScroll();

    const bodyEl = gridBodyRef.current;
    const headerEl = gridHeaderRef.current;
    if (!bodyEl) return;

    // Guard: discard if user has already navigated away to a different page
    if (targetPage !== paginationStateRef.current.currentPage) {
      return;
    }

    isSyncingRef.current = true;
    paginationStateRef.current.isPaginating = true;
    isPaginationRef.current = true;

    let executed = false;
    const id = requestAnimationFrame(() => {
      executed = true;
      rafIdRef.current = null;

      // Re-check guard in case page changed before RAF fired
      if (targetPage !== paginationStateRef.current.currentPage) {
        isSyncingRef.current = false;
        paginationStateRef.current.isPaginating = false;
        isPaginationRef.current = false;
        return;
      }

      bodyEl.scrollLeft = targetScroll;
      if (headerEl) headerEl.scrollLeft = targetScroll;
      lastScrollLeftRef.current = targetScroll;
      savedScrollRef.current = targetScroll;
      paginationStateRef.current.savedScroll = targetScroll;
      setScrollLeft(targetScroll);
      onTimelineScrollChange?.(targetScroll);

      paginationStateRef.current.lastAppliedPage = targetPage;
      paginationStateRef.current.lastAppliedTasks = currentTasks;

      timeoutIdRef.current = setTimeout(() => {
        timeoutIdRef.current = null;
        if (targetPage !== paginationStateRef.current.currentPage) {
          return;
        }
        isSyncingRef.current = false;
        paginationStateRef.current.isPaginating = false;
        isPaginationRef.current = false;
      }, 50);
    });

    if (!executed) {
      rafIdRef.current = id;
    }
  }, [onTimelineScrollChange, cancelPendingScroll, currentTasks]);

  // Retain horizontal timeline position or auto-navigate to date range containing newly selected page's tickets
  useEffect(() => {
    const currentPage = pagination?.currentPage;
    if (currentPage === undefined) return;

    // Detect page change
    if (currentPage !== paginationStateRef.current.currentPage) {
      cancelPendingScroll();
      const bodyEl = gridBodyRef.current;
      const currentScroll = (bodyEl && bodyEl.scrollLeft > 0)
        ? bodyEl.scrollLeft
        : (lastScrollLeftRef.current || initialScrollLeft || 0);

      paginationStateRef.current.currentPage = currentPage;
      paginationStateRef.current.savedScroll = currentScroll;
      savedScrollRef.current = currentScroll;
      paginationStateRef.current.isPaginating = true;
      isPaginationRef.current = true;
    }

    // Only apply pagination scroll if pagination transition is active
    // and hasn't already been applied for this (currentPage, tasks) pair
    if (!paginationStateRef.current.isPaginating) return;
    if (
      paginationStateRef.current.lastAppliedPage === currentPage &&
      paginationStateRef.current.lastAppliedTasks === currentTasks
    ) {
      return;
    }

    const bodyEl = gridBodyRef.current;
    if (!bodyEl) return;

    const savedScroll = paginationStateRef.current.savedScroll;
    const viewportWidth = bodyEl.clientWidth || 800;
    const viewportStart = savedScroll;
    const viewportEnd = savedScroll + viewportWidth;

    // Collect all occupied column ranges for tasks on the newly selected page
    const coveredColumns = new Set();

    const collectTaskCols = (taskList) => {
      if (!Array.isArray(taskList)) return;
      taskList.forEach(task => {
        const range = getTaskColumnRange(task, dates, viewType);
        if (range && !range.isOutside) {
          for (let c = range.startCol; c <= range.endCol; c++) {
            coveredColumns.add(c);
          }
        }
        if (task.subtasks?.length) {
          collectTaskCols(task.subtasks);
        }
      });
    };

    collectTaskCols(currentTasks);

    // Check if any covered column is already visible in the viewport
    let isAnyTaskVisible = false;
    for (const colIndex of coveredColumns) {
      const metrics = getColumnMetrics(colIndex);
      if (metrics) {
        const colLeft = metrics.left;
        const colRight = metrics.left + metrics.width;
        if (colRight > viewportStart + 20 && colLeft < viewportEnd - 20) {
          isAnyTaskVisible = true;
          break;
        }
      }
    }

    let targetScroll = savedScroll;

    // If no task on this page is visible in the current viewport and tasks exist on timeline:
    // Auto-navigate to the date range containing the tickets
    if (!isAnyTaskVisible && coveredColumns.size > 0) {
      const sortedCols = Array.from(coveredColumns).sort((a, b) => a - b);
      const targetCol = sortedCols[0];
      const targetMetrics = getColumnMetrics(targetCol);
      if (targetMetrics) {
        targetScroll = Math.max(0, targetMetrics.left - 24);
      }
    }

    applyScrollToTarget(targetScroll, currentPage);

    return () => {
      cancelPendingScroll();
    };
  }, [
    pagination?.currentPage,
    currentTasks,
    dates,
    viewType,
    initialScrollLeft,
    getColumnMetrics,
    applyScrollToTarget,
    cancelPendingScroll
  ]);

  // Cleanup pending scroll operations on unmount
  useEffect(() => {
    return () => {
      cancelPendingScroll();
    };
  }, [cancelPendingScroll]);

  const getColumnContentMetrics = useCallback((colIndex) => {
    const bodyEl = gridBodyRef.current;
    if (!bodyEl) return null;

    const columnEl = bodyEl.querySelector(`[data-column-index="${colIndex}"]`);
    if (!columnEl) return null;

    const rect = columnEl.getBoundingClientRect();
    const styles = getComputedStyle(columnEl);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;

    return {
      left: columnEl.offsetLeft + paddingLeft,
      width: Math.max(0, rect.width - paddingLeft - paddingRight)
    };
  }, []);

  const getDatePositionInMonth = useCallback((dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split('-').map(Number);
    if (parts.length < 3) return 0;
    const [, month, day] = parts;
    const daysInMonth = new Date(parts[0], month, 0).getDate();
    if (daysInMonth <= 1) return 0;
    return (day - 1) / (daysInMonth - 1);
  }, []);

  const getCardPositionFromMetrics = useCallback((card, viewTypeOverride = null) => {
    const startMetrics = getColumnContentMetrics(card.startCol) || getColumnMetrics(card.startCol);
    const endMetrics = getColumnContentMetrics(card.endCol) || getColumnMetrics(card.endCol);
    if (!startMetrics || !endMetrics) return null;

    if (card.spanWidth <= 1) {
      const inset = 5;
      return {
        left: `${startMetrics.left + inset}px`,
        width: `${Math.max(0, startMetrics.width - inset * 2)}px`
      };
    }

    let earliestStart = card.tasks[0]?.startDate;
    let latestDue = card.tasks[0]?.dueDate;
    for (let i = 1; i < card.tasks.length; i++) {
      const taskStart = card.tasks[i].startDate;
      const taskDue = card.tasks[i].dueDate;
      if (taskStart && (!earliestStart || taskStart < earliestStart)) earliestStart = taskStart;
      if (taskDue && (!latestDue || taskDue > latestDue)) latestDue = taskDue;
    }
    if (!earliestStart || !latestDue) return null;

    const [endYear, endMonth] = latestDue.split('-').map(Number);

    let startOffset = getDatePositionInMonth(earliestStart) * startMetrics.width;
    let endOffset = getDatePositionInMonth(latestDue) * endMetrics.width;

    let startBase = startMetrics.left;
    if (card.boundaryInfo?.isIncoming) {
      startOffset = 0;
    }
    let endBase;

    if (card.boundaryInfo?.isOutgoing) {
      const endMonthStr = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
      const endColIndex = dates.findIndex(date => date.substring(0, 7) === endMonthStr.substring(0, 7));
      if (endColIndex === -1) {
        const lastMetrics = getColumnMetrics(dates.length - 1);
        endBase = lastMetrics ? lastMetrics.left : endMetrics.left;
        endOffset = lastMetrics ? lastMetrics.width : endMetrics.width;
      } else {
        const endColMetrics = getColumnMetrics(endColIndex);
        endBase = endColMetrics ? endColMetrics.left : endMetrics.left;
        endOffset = endColMetrics ? endColMetrics.width : endMetrics.width;
      }
    } else {
      endBase = endMetrics.left;
    }

    const inset = 5;
    const left = startBase + startOffset + inset;
    let width = (endBase - startBase) + endOffset - startOffset - inset * 2;
    if (card.boundaryInfo?.isOutgoing) {
      width = Math.max(0, width - inset);
    }

    return {
      left: `${left}px`,
      width: `${Math.max(0, width)}px`
    };
  }, [dates, getColumnContentMetrics, getColumnMetrics, getDatePositionInMonth]);

  const isWeekDragging = viewType === GANTT_VIEW_TYPES.WEEK && (dragState || resizeState);
  let endDropInfo = null;

  if (isWeekDragging && dragState?.dragType === 'move' && weekDropZone && dragState.task) {
    const startDateStr = weekDropZone.targetDate || null;
    if (startDateStr) {
      const taskDuration = calculateTaskDuration(dragState.task.startDate, dragState.task.dueDate);
      const startDateObj = parseDateLocal(startDateStr);
      const endDateObj = new Date(startDateObj);
      endDateObj.setDate(startDateObj.getDate() + taskDuration - 1);
      const endDateStr = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;
      endDropInfo = getWeekColumnDay(endDateStr);
    }
  }

  const safeDates = Array.isArray(dates) ? dates : [];
  const safeHeaders = Array.isArray(headers) ? headers : [];
  const isYearView = viewType === GANTT_VIEW_TYPES.YEAR;
  const isQuarterView = viewType === GANTT_VIEW_TYPES.QUARTER;
  const isWeekView = viewType === GANTT_VIEW_TYPES.WEEK;
  const isDayView = viewType === GANTT_VIEW_TYPES.DAY;
  const isCardView = isQuarterView || isYearView;

  return (
    <Box className="gantt-grid">
      {/* Header wrapper for year view alignment */}
      <Box
        className={`gantt-header-wrapper ${isYearView ? 'year-view' : ''} ${isWeekView ? 'week-view' : ''} ${isDayView ? 'day-view' : ''} ${isQuarterView ? 'quarter-view' : ''}`}
        style={{ paddingRight: `${scrollbarWidth}px` }}
      >
        <Box
          className={`gantt-grid-header transparent-scrollbar ${isYearView ? 'year-view' : ''} ${isWeekView ? 'week-view' : ''} ${isDayView ? 'day-view' : ''} ${isQuarterView ? 'quarter-view' : ''}`}
          ref={gridHeaderRef}
        >
          {safeHeaders.map((header, index) => (
            <Box key={header || `header-${index}`} className="gantt-grid-header-cell">
              {header}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Today Indicator Container - positioned outside scrolling body */}
      {todayBasePosition.isVisible && (
        <Box className="gantt-today-indicator-container">
          <Box
            className="gantt-today-indicator"
            style={{
              left: todayBasePosition.position,
              transform: `translateX(-${scrollLeft}px)`
            }}
          />
        </Box>
      )}

      <Box
        className={`gantt-grid-body custom-scrollbar ${isYearView ? 'year-view' : ''} ${isWeekView ? 'week-view' : ''} ${isDayView ? 'day-view' : ''}`}
        ref={gridBodyRef}
      >
        
        {/* Dependency Arrows */}
        <GanttDependencyArrows
          tasks={flattenedTasks}
          tasksByColumn={tasksByColumn}
          dates={dates}
          viewType={viewType}
          containerRef={gridBodyRef}
          isEnabled={configOptions.dependencies}
        />
        
        {safeDates.map((date, colIndex) => {
          const columnKey = date ? `date-${date}` : `col-${colIndex}`;
          return (
          <Box 
            key={columnKey} 
            data-column-index={colIndex}
            className={`gantt-grid-column ${dropZone === colIndex ? 'drop-zone-active' : ''} ${isCardView ? 'card-view' : ''} ${isYearView ? 'year-view' : ''} ${isQuarterView ? 'quarter-view' : ''} ${isWeekView ? 'week-view' : ''} ${isDayView ? 'day-view' : ''}`}
          >
            {/* Week view day-level drop zones - show when dragging or resizing */}
            {isWeekDragging && (dropZone === colIndex || endDropInfo?.columnIndex === colIndex) && (
              <Box className="week-drop-zones">
                {/* Calculate drop zone position relative to this specific column */}
                {dropZone === colIndex && weekDropZone && (() => {
                  // Use the day index directly from weekDropZone since it's already calculated correctly
                  const clampedDayIndex = Math.max(0, Math.min(weekDropZone.dayIndex, 6));
                  
                  
                  return (
                    <Box
                      className="week-day-drop-zone active"
                      style={{
                        left: `${(clampedDayIndex * 100) / 7}%`,
                        width: `${100 / 7}%`
                      }}
                    >
                      <Box className="drop-zone-indicator">
                        <Box className="drop-zone-highlight" />
                        <Box 
                          className="drop-zone-label"
                          style={{
                            top: calculateLabelPosition(dragState?.taskId || resizeState?.taskId)
                          }}
                        >
                          {(() => {
                            // Calculate the date for this day
                            const weekStartDate = new Date(date);
                            const targetDate = new Date(weekStartDate);
                            targetDate.setDate(weekStartDate.getDate() + clampedDayIndex);
                            
                            const dayNumber = targetDate.getDate();
                            const monthName = targetDate.toLocaleDateString('en-US', { month: 'short' });
                            
                            return `${dayNumber} ${monthName}`;
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}
                
                {/* For move operations, also show the end date drop zone */}
                {dragState?.dragType === 'move' && endDropInfo && endDropInfo.columnIndex === colIndex && (
                  (() => {
                    const endDayIndex = Math.max(0, Math.min(endDropInfo.dayIndex, 6));
                    if (dropZone === colIndex && weekDropZone && endDropInfo.columnIndex === colIndex && endDayIndex === weekDropZone.dayIndex) {
                      return null;
                    }

                    const weekStartDate = parseDateLocal(date);
                    const endTargetDate = new Date(weekStartDate);
                    endTargetDate.setDate(weekStartDate.getDate() + endDayIndex);
                    
                    const endDayNumber = endTargetDate.getDate();
                    const endMonthName = endTargetDate.toLocaleDateString('en-US', { month: 'short' });
                    
                    return (
                      <Box
                        className="week-day-drop-zone end-date"
                        style={{
                          left: `${(endDayIndex * 100) / 7}%`,
                          width: `${100 / 7}%`
                        }}
                      >
                        <Box className="drop-zone-indicator">
                          <Box className="drop-zone-highlight" />
                          <Box 
                            className="end-date-label"
                            style={{
                              top: calculateLabelPosition(dragState?.taskId)
                            }}
                          >
                            {`${endDayNumber} ${endMonthName}`}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()
                )}
              </Box>
            )}
          </Box>
        );
        })}

        {isCardView ? null : (
          <Box className="gantt-capsules-container">
            {safeDates.map((date, colIndex) =>
              (tasksByColumn[colIndex] || []).map((item) => {
                const isBeingDragged = dragState?.taskId === item.task.id;
                const isBeingResized = resizeState?.taskId === item.task.id;

                if (viewType === GANTT_VIEW_TYPES.WEEK && item.startCol !== colIndex) {
                  return null;
                }

                const metrics = getColumnMetrics(colIndex);
                if (!metrics) return null;

                let taskStyle;

                if (viewType === GANTT_VIEW_TYPES.WEEK) {
                  const { startDate, dueDate } = getTaskDates(item.task);
                  const taskStart = parseDateLocal(startDate);
                  const taskEnd = parseDateLocal(dueDate);
                  const isSingleDay = taskStart && taskEnd && taskStart.getTime() === taskEnd.getTime();
                  const weekStart = parseDateLocal(date);
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);

                  const viewEnd = parseDateLocal(dates[dates.length - 1]);
                  viewEnd.setDate(viewEnd.getDate() + 6);

                  const startForWidth = taskStart < weekStart ? weekStart : taskStart;
                  const endForWidth = taskEnd > viewEnd ? viewEnd : taskEnd;

                  const dayOfWeek = startForWidth.getDay();
                  const startMondayBasedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

                  const contentInset = 15;
                  const contentWidth = Math.max(0, metrics.width - contentInset * 2);
                  const dayWidthPx = contentWidth / 7;
                  const MIN_SINGLE_DAY_WIDTH = 130;
                  const endDayOfWeek = endForWidth.getDay();
                  const endMondayBasedDay = endDayOfWeek === 0 ? 6 : endDayOfWeek - 1;

                  const startWeekIndex = item.startCol ?? colIndex;
                  const endWeekIndex = item.endCol ?? startWeekIndex;

                  const startMetrics = metrics;
                  const endMetrics = getColumnMetrics(endWeekIndex) || metrics;
                  const endContentWidth = Math.max(0, endMetrics.width - contentInset * 2);
                  const dayWidthStart = contentWidth / 7;
                  const dayWidthEnd = endContentWidth / 7;

                  let leftPx = startMetrics.left + contentInset + startMondayBasedDay * dayWidthStart;
                  const endPx = endMetrics.left + contentInset + (endMondayBasedDay + 1) * dayWidthEnd;

                  const baseWidthPx = Math.max(dayWidthStart, endPx - leftPx);

                  // Ensure minimum width for one-day tasks regardless of grouping
                  let widthPx;
                  if (isSingleDay) {
                    const minLeft = startMetrics.left + contentInset;
                    const maxRight = startMetrics.left + contentInset + contentWidth;
                    const desiredWidth = Math.max(dayWidthPx, MIN_SINGLE_DAY_WIDTH);
                    widthPx = desiredWidth;
                    if (leftPx + widthPx > maxRight) {
                      leftPx = Math.max(minLeft, maxRight - widthPx);
                    }
                    if (leftPx < minLeft) {
                      leftPx = minLeft;
                    }
                    if (leftPx + widthPx > maxRight) {
                      widthPx = Math.max(0, maxRight - leftPx);
                    }
                  } else {
                    widthPx = (baseWidthPx <= dayWidthPx)
                      ? Math.max(dayWidthPx, MIN_SINGLE_DAY_WIDTH)
                      : baseWidthPx;
                  }

                  taskStyle = {
                    top: `${item.rowIndex * (36 + 40)}px`,
                    left: `${leftPx}px`,
                    width: `${widthPx}px`,
                    zIndex: 10
                  };
                } else {
                  const { startDate, dueDate } = getTaskDates(item.task);
                  const taskStart = parseDateLocal(startDate);
                  const taskEnd = parseDateLocal(dueDate);
                  const marginOffset = 30;

                  let visibleStartIndex = -1;
                  let visibleEndIndex = -1;
                  dates.forEach((d, i) => {
                    const day = parseDateLocal(d);
                    if (taskStart <= day && taskEnd >= day) {
                      if (visibleStartIndex === -1) visibleStartIndex = i;
                      visibleEndIndex = i;
                    }
                  });

                  if (visibleStartIndex === -1 && taskStart < parseDateLocal(dates[0])) {
                    visibleStartIndex = 0;
                  }
                  if (visibleEndIndex === -1 && taskEnd > parseDateLocal(dates[dates.length - 1])) {
                    visibleEndIndex = dates.length - 1;
                  }
                  if (visibleStartIndex === -1 && visibleEndIndex === -1) {
                    if (taskStart < parseDateLocal(dates[0]) && taskEnd > parseDateLocal(dates[dates.length - 1])) {
                      visibleStartIndex = 0;
                      visibleEndIndex = dates.length - 1;
                    }
                  }

                  const spanDays = visibleStartIndex !== -1 && visibleEndIndex !== -1
                    ? visibleEndIndex - visibleStartIndex + 1
                    : 1;

                  const leftPx = metrics.left + 15;
                  const widthPx = spanDays * metrics.width + (spanDays - 1) * 1 - marginOffset;
                  taskStyle = {
                    top: `${item.rowIndex * (36 + 40)}px`,
                    left: `${leftPx}px`,
                    width: `${widthPx}px`
                  };
                }

                return (
                  <GanttTaskCapsule
                    key={`${item.task.id}-${colIndex}`}
                    task={item.task}
                    dates={dates}
                    viewType={viewType}
                    onClick={() => onTaskClick?.(item.task)}
                    onTaskUpdate={handleTaskUpdate}
                    onDragStart={handleDragStart}
                    onDragUpdate={handleDragUpdate}
                    onDragEnd={handleDragEnd}
                    colorIndex={parseInt(item.task.id)}
                    containerRef={gridBodyRef}
                    isBeingDragged={isBeingDragged}
                    isBeingResized={isBeingResized}
                    resizeState={resizeState}
                    boundaryInfo={item.boundaryInfo}
                    onBoundaryClick={handleBoundaryTaskClick}
                    style={taskStyle}
                    groupByOption={groupByOption}
                    incomingDependencies={incomingDependenciesMap.get(String(item.task.id)) || []}
                    taskByIdMap={taskByIdMap}
                    projectId={projectId}
                    dependenciesEnabled={configOptions?.dependencies}
                    activeTooltipTaskId={activeTooltipTaskId}
                    setActiveTooltipTaskId={setActiveTooltipTaskId}
                  />
                );
              })
            )}
          </Box>
        )}
        
        {/* Render cards for Quarter and Year views outside of columns */}
        {(viewType === GANTT_VIEW_TYPES.QUARTER || viewType === GANTT_VIEW_TYPES.YEAR) && (
          <Box className="gantt-cards-container">
            {Object.values(tasksByColumn).flat().map((card) => {
              let leftPosition, cardWidth;
              
              const metricPosition = getCardPositionFromMetrics(card);
              if (metricPosition) {
                leftPosition = metricPosition.left;
                cardWidth = metricPosition.width;
              } else if (card.spanWidth === 1) {
                // Single month cards: Keep existing logic (centered in column)
                if (viewType === GANTT_VIEW_TYPES.YEAR) {
                  const totalColumns = dates.length;
                  const minColumnWidth = 180;
                  const containerWidth = gridBodyRef.current?.scrollWidth || (totalColumns * minColumnWidth);
                  const actualColumnWidth = Math.max(minColumnWidth, containerWidth / totalColumns);
                  
                  const leftPx = card.startCol * actualColumnWidth + 5;
                  const widthPx = actualColumnWidth - 10;
                  leftPosition = `${leftPx}px`;
                  cardWidth = `${widthPx}px`;
                } else {
                  const columnWidth = 100 / dates.length;
                  leftPosition = `calc(${card.startCol * columnWidth}% + 5px)`;
                  cardWidth = `calc(${columnWidth}% - 10px)`;
                }
              } else {
                // Multi-month cards: Use precise date-based positioning
                if (viewType === GANTT_VIEW_TYPES.YEAR) {
                  const totalColumns = dates.length;
                  const minColumnWidth = 180;
                  const containerWidth = gridBodyRef.current?.scrollWidth || (totalColumns * minColumnWidth);
                  const actualColumnWidth = Math.max(minColumnWidth, containerWidth / totalColumns);
                  
                  const position = calculateMultiMonthPosition(card, dates, viewType, actualColumnWidth);
                  leftPosition = position.leftPosition;
                  cardWidth = position.cardWidth;
                  // Use pre-calculated boundary info from card creation
                } else {
                  const position = calculateMultiMonthPosition(card, dates, viewType);
                  leftPosition = position.leftPosition;
                  cardWidth = position.cardWidth;
                  // Use pre-calculated boundary info from card creation
                }
              }

              return (
                <GanttTaskCard
                  key={card.id}
                  tasks={card.tasks}
                  totalTasks={card.totalTasks}
                  colorIndex={card.colorIndex}
                  boundaryInfo={card.boundaryInfo}
                  onBoundaryClick={handleBoundaryTaskClick}
                  onClick={(task) => onTaskClick?.(task)}
                  groupByOption={groupByOption}
                  activeTooltipTaskId={activeTooltipTaskId}
                  setActiveTooltipTaskId={setActiveTooltipTaskId}
                  dates={dates}
                  viewType={viewType}
                  style={{
                    position: 'absolute',
                    top: `${card.topPosition || (card.rowIndex * 60 + 8)}px`,
                    left: leftPosition,
                    width: cardWidth,
                    zIndex: card.spanWidth > 1 ? 2 : 1
                  }}
                />
              );
            })}
          </Box>
        )}
        
        {dragState?.isDragging && dragState?.dragType === 'move' && (
          <Box
            className={`gantt-drag-shadow ${dragState.colorClass}`}
            style={{
              left: `${dragState.shadowX}px`,
              top: `${dragState.shadowY}px`,
              width: `${dragState.shadowWidth}px`
            }}
          >
            <Box className="gantt-task-capsule-content">
              <Typography className="gantt-task-capsule-text" variant="body2">
                {dragState.task?.ticketNumber} {dragState.task?.title}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GanttGrid;
